// CAO Studio logo geometry: the one place the mark and wordmark are drawn. Logo.tsx, the SVG files in
// public/brand/ and docs/brand/, pwa/icon.svg and every rendered PNG come from these numbers (tools/build.ts),
// so they never drift.
//
// The mark (brand v2, Keaton's concept, 2026-10-30): a minimal director's chair from the front. Two upright posts
// with rounded tops; between them a seat back in Tungsten, slightly curved like canvas pulled round a cylinder; below,
// the folding legs as two flat ribbons crossing in an X, cut vertically at their ends. The leg running top right to
// bottom left passes over the other, which takes a clean gap and a slightly darker shade. Nothing else: no seat, no
// arms, no stitching.
//
// Two drawings, both in a 64 x 64 box: the full chair, and a heavier small chair for 32 px and below (on a 4-unit
// grid, so at 16 px its posts, back and gaps land on whole pixels; the legs cross without a gap).
//
// The wordmark: "CAO" is drawn here from geometry (an open C and a round O, slightly wide ellipses; the A as a Λ with
// no crossbar), "STUDIO" is Montserrat 500 outlined by tools/wordmark.mjs (SIL OFL), set small and widely tracked,
// centred under CAO. Word units: CAO's cap height is 100, cap top at y 0, baseline at y 100.
import phrases from "./wordmark.json";

/** The mark's box, in chair units. */
export const MARK_BOX = 64;

type Pt = readonly [number, number];
const r2 = (n: number) => Math.round(n * 100) / 100;
const P = (q: Pt) => `${r2(q[0])},${r2(q[1])}`;

/** A polygon as path data with each corner rounded (quadratic fillet) by r[i] (or r for all). */
function rounded(pts: readonly Pt[], r: number | readonly number[]): string {
  const n = pts.length;
  let d = "";
  for (let i = 0; i < n; i++) {
    const p = pts[(i - 1 + n) % n]!;
    const c = pts[i]!;
    const q = pts[(i + 1) % n]!;
    const l1 = Math.hypot(c[0] - p[0], c[1] - p[1]);
    const l2 = Math.hypot(q[0] - c[0], q[1] - c[1]);
    const rr = Math.min(typeof r === "number" ? r : (r[i] ?? 0), l1 / 2, l2 / 2);
    const a: Pt = [c[0] - ((c[0] - p[0]) / l1) * rr, c[1] - ((c[1] - p[1]) / l1) * rr];
    const b: Pt = [c[0] + ((q[0] - c[0]) / l2) * rr, c[1] + ((q[1] - c[1]) / l2) * rr];
    d += `${i === 0 ? "M" : "L"}${P(a)}${rr > 0 ? `Q${P(c)} ${P(b)}` : ""}`;
  }
  return `${d}Z`;
}

/** Keep the part of a convex polygon on one side of the line through p along direction u, offset by `off`. */
function clip(pts: readonly Pt[], p: Pt, u: Pt, off: number, side: 1 | -1): Pt[] {
  const L = Math.hypot(u[0], u[1]);
  const f = (q: Pt) => side * (((q[0] - p[0]) * -u[1] + (q[1] - p[1]) * u[0]) / L) - off; // >= 0 is kept
  const out: Pt[] = [];
  pts.forEach((a, i) => {
    const b = pts[(i + 1) % pts.length]!;
    const fa = f(a);
    const fb = f(b);
    if (fa >= 0) out.push(a);
    if (fa >= 0 !== fb >= 0) {
      const t = fa / (fa - fb);
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  });
  return out;
}

/** The chair's parts as SVG path data. `posts` and `over` take the ink colour, `under` its shade, `back` Tungsten. */
export interface ChairArt {
  /** Ink box [x1, y1, x2, y2] in chair units. */
  bbox: readonly [number, number, number, number];
  posts: readonly string[];
  /** The leg on top (top right to bottom left). */
  over: string;
  /** The leg underneath, in one piece or two (cut by the gap round the over leg). */
  under: readonly string[];
  /** The seat back. */
  back: string;
  /** The seat back's box [x1, y1, x2, y2] (for the animation's pivot and the clear-space rule). */
  backBox: readonly [number, number, number, number];
}

interface ChairSpec {
  x0: number; // outer left edge (the chair is symmetric about x 32)
  top: number; // post tops
  floor: number;
  postW: number;
  postR: number; // the posts' top corners
  gap: number; // perpendicular gap between parts
  backInset: number; // gap between a post and the back
  backTop: number; // the back's top and bottom at its ends, and how far each edge sags in the middle
  backBottom: number;
  sagTop: number;
  sagBottom: number;
  legTop: number; // the under leg's top edge at the outer left end
  legT: number; // a leg's vertical thickness
  legGap: boolean; // cut the gap round the over leg
  r: number; // the legs' and posts' small corners
}

function buildChair(s: ChairSpec): ChairArt {
  const x1 = MARK_BOX - s.x0;
  const xe = x1 - s.postW; // the under leg ends under the right post's inner edge
  const slope = (s.floor - s.legT - s.legTop) / (xe - s.x0);
  const cos = 1 / Math.hypot(1, slope);
  const under: Pt[] = [
    [s.x0, s.legTop],
    [xe, s.floor - s.legT],
    [xe, s.floor],
    [s.x0, s.legTop + s.legT],
  ];
  const mirror = (pts: readonly Pt[]) => pts.map(([x, y]) => [MARK_BOX - x, y] as Pt);
  const over = mirror(under);
  // the posts stand on the legs' top edges: their feet are cut parallel to the leg, `gap` clear of it
  const gv = s.gap / cos;
  const footL = (x: number) => s.legTop + slope * (x - s.x0) - gv;
  const postL: Pt[] = [
    [s.x0, s.top],
    [s.x0 + s.postW, s.top],
    [s.x0 + s.postW, footL(s.x0 + s.postW)],
    [s.x0, footL(s.x0)],
  ];
  const postRadii = [s.postR, s.postR, s.r, s.r];
  // the gap round the over leg: its centre line, and half its perpendicular thickness plus the gap
  let underPieces: Pt[][] = [under];
  if (s.legGap) {
    const c: Pt = [MARK_BOX - s.x0, s.legTop + s.legT / 2];
    const u: Pt = [-(xe - s.x0), slope * (xe - s.x0)];
    const half = (s.legT / 2) * cos + s.gap;
    underPieces = [clip(under, c, u, half, 1), clip(under, c, u, half, -1)].filter((p) => p.length >= 3);
  }
  const bx1 = s.x0 + s.postW + s.backInset;
  const bx2 = MARK_BOX - bx1;
  const back = `M${P([bx1, s.backTop])}Q${P([32, s.backTop + 2 * s.sagTop])} ${P([bx2, s.backTop])}L${P([bx2, s.backBottom])}Q${P([32, s.backBottom + 2 * s.sagBottom])} ${P([bx1, s.backBottom])}Z`;
  return {
    bbox: [s.x0, s.top, x1, s.floor],
    posts: [rounded(postL, postRadii), rounded(mirror(postL), postRadii)],
    over: rounded(over, s.r),
    under: underPieces.map((p) => rounded(p, s.r * 0.8)),
    back,
    backBox: [bx1, s.backTop, bx2, s.backBottom + s.sagBottom],
  };
}

/** The full chair (above 32 px). Measured from Keaton's concept art and rounded to clean numbers. */
export const CHAIR: ChairArt = buildChair({
  x0: 1.5,
  top: 4,
  floor: 60,
  postW: 5.3,
  postR: 1.7,
  gap: 1.25,
  backInset: 1.45,
  backTop: 8.2,
  backBottom: 19,
  sagTop: 1,
  sagBottom: 1.7,
  legTop: 21.9,
  legT: 6,
  legGap: true,
  r: 0.55,
});

/** The small chair (32 px and below): at 16 px the posts are 2 px, the back 3 px, the gaps 1 px, the legs ~1.7 px. */
export const CHAIR_SMALL: ChairArt = buildChair({
  x0: 2,
  top: 4,
  floor: 60,
  postW: 8,
  postR: 2.4,
  gap: 3.6,
  backInset: 4,
  backTop: 8,
  backBottom: 20,
  sagTop: 0,
  sagBottom: 0,
  legTop: 28,
  legT: 8,
  legGap: false, // under a pixel wide it would only turn the crossing grey
  r: 1,
});

/**
 * The tile chair: the small chair drawn inside an app-icon tile at 48 px and below (favicons, small tiles). It sits in
 * the box's centre 12 x 12 px at 16 px (a 4-unit grid, clear of the tile's rounded corners): posts 2 px, back 6 x 3 px,
 * 1 px gaps, legs 2 px.
 */
export const CHAIR_TILE: ChairArt = buildChair({
  x0: 8,
  top: 8,
  floor: 56,
  postW: 8,
  postR: 2.4,
  gap: 3.6,
  backInset: 4,
  backTop: 12,
  backBottom: 24,
  sagTop: 0,
  sagBottom: 0,
  legTop: 28,
  legT: 8,
  legGap: false,
  r: 1,
});

/** The full chair's scale on an app-icon tile (its ink in the centre ~60%, inside the maskable safe zone). */
export const TILE_SCALE = 0.62;

/** At this rendered size and below (in px, the chair's height) the small chair is drawn. */
export const SMALL_MAX_PX = 32;
export const chairFor = (px: number | string | undefined) => (isSmall(px) ? CHAIR_SMALL : CHAIR);
function isSmall(px: number | string | undefined) {
  if (typeof px === "number") return px <= SMALL_MAX_PX;
  const m = typeof px === "string" ? /^\s*([\d.]+)\s*(px)?\s*$/.exec(px) : null;
  return m ? Number(m[1]) <= SMALL_MAX_PX : false;
}

/* ------------------------------------------------------------------ the wordmark */

/** An outlined Montserrat phrase from wordmark.json (font units, baseline 0, y down). */
export type Phrase = { key: string; text: string; d: string; advance: number; bounds: [number, number, number, number]; cap: number; upm: number; font: string; weight: number; tracking: number };
const PHRASES = phrases as unknown as Phrase[];
export const phrase = (key: string): Phrase => {
  const p = PHRASES.find((x) => x.key === key);
  if (!p) throw new Error(`wordmark.json has no phrase "${key}"`);
  return p;
};

/** CAO's letters, in word units (cap height 100, cap top y 0, baseline y 100). */
const STEM_V = 20.5; // vertical stroke of the round letters
const STEM_H = 18.5; // their horizontal stroke (thinner, as in a geometric sans)
const ROUND = { rx: 56, ry: 51.5, cy: 50 }; // C and O: slightly wide, overshooting the cap height by 1.5
const LAMBDA = { w: 129, apex: 19, stem: 20 }; // Λ: base width, flat apex width, perpendicular stroke
const C_OPEN = 33; // the C's terminals, degrees from the horizontal
const TRACK = [41, 40]; // C to Λ, Λ to O

function ellipsePt(cx: number, cy: number, rx: number, ry: number, deg: number): Pt {
  const t = (deg * Math.PI) / 180;
  return [cx + rx * Math.cos(t), cy + ry * Math.sin(t)];
}

export const CAO = (() => {
  const { rx, ry, cy } = ROUND;
  const irx = rx - STEM_V;
  const iry = ry - STEM_H;
  // C: from the top terminal round the left to the bottom one, radial cuts
  const cx = rx;
  const o1 = ellipsePt(cx, cy, rx, ry, -C_OPEN);
  const o2 = ellipsePt(cx, cy, rx, ry, C_OPEN);
  const i2 = ellipsePt(cx, cy, irx, iry, C_OPEN);
  const i1 = ellipsePt(cx, cy, irx, iry, -C_OPEN);
  const c = `M${P(o1)}A${rx},${ry} 0 1 0 ${P(o2)}L${P(i2)}A${r2(irx)},${r2(iry)} 0 1 1 ${P(i1)}Z`;
  const cW = o1[0];
  // Λ: outer edges from the base corners to the flat apex; inner edges parallel, meeting in a point
  const ax = cW + TRACK[0]!;
  const { w, apex, stem } = LAMBDA;
  const run = (w - apex) / 2;
  const wh = stem / Math.sin(Math.atan2(100, run)); // horizontal width of a leg
  const innerY = 100 - ((w / 2 - wh) * 100) / run;
  const lam = `M${P([ax, 100])}L${P([ax + run, 0])}L${P([ax + run + apex, 0])}L${P([ax + w, 100])}L${P([ax + w - wh, 100])}L${P([ax + w / 2, innerY])}L${P([ax + wh, 100])}Z`;
  // O: a ring (even-odd)
  const ox = ax + w + TRACK[1]! + rx;
  const ring = (x: number, a: number, b: number) => `M${r2(x - a)},${cy}a${r2(a)},${b} 0 1 0 ${r2(2 * a)},0a${r2(a)},${b} 0 1 0 ${r2(-2 * a)},0Z`;
  const o = `${ring(ox, rx, ry)}${ring(ox, irx, iry)}`;
  return { d: `${c}${lam}${o}`, width: r2(ox + rx), top: cy - ry, bottom: cy + ry };
})();

/** STUDIO's cap height in word units, and the gap from CAO's baseline to STUDIO's cap top. */
const STUDIO_CAP = 29;
const STUDIO_GAP = 33;
/** A phrase scaled to a cap height (word units) with its ink centred on x = cx and its baseline at y. */
export function placePhrase(p: Phrase, cap: number, cx: number, baseline: number) {
  const k = cap / p.cap;
  const w = (p.bounds[2] - p.bounds[0]) * k;
  const x = cx - w / 2 - p.bounds[0] * k;
  return { transform: `translate(${r2(x)} ${r2(baseline)}) scale(${Math.round(k * 1e6) / 1e6})`, width: w, x: x + p.bounds[0] * k, k };
}
export const STUDIO = phrase("studio");
export const TAGLINE = phrase("tagline");

/** The two-line wordmark (CAO over STUDIO) in word units, ink from x 0. */
export const WORDMARK = (() => {
  const studioBase = 100 + STUDIO_GAP + STUDIO_CAP;
  const studio = placePhrase(STUDIO, STUDIO_CAP, CAO.width / 2, studioBase);
  const bottom = studioBase + STUDIO.bounds[3] * studio.k;
  const top = CAO.top;
  return { width: CAO.width, top, bottom, height: bottom - top, studio: studio.transform, viewBox: `0 ${r2(top)} ${r2(CAO.width)} ${r2(bottom - top)}` };
})();
/** Legacy names: the words as paths (CAO at x 0; STUDIO already placed by WORDMARK.studio). */
export const WORD_CAO = { d: CAO.d };
export const WORD_STUDIO = { d: STUDIO.d };
/** CAO's cap height in word units. */
export const CAP = 100;

export interface Lockup {
  viewBox: string;
  width: number;
  height: number;
  /** Transform that places the 64-unit chair box. */
  mark: string;
  /** Transform that places the wordmark (CAO at its own origin; STUDIO inside it via WORDMARK.studio). */
  word: string;
  /** The chair's scale (word units per chair unit). */
  k: number;
}

/** Horizontal lockup: the chair, then the two-line wordmark, centred on it. The chair is the full height; the
 *  wordmark is 0.8 of it; the gap is 0.3 of it. Word units. */
export function horizontalLockup(chair: ChairArt = CHAIR): Lockup {
  const H = WORDMARK.height / 0.8;
  const k = H / (chair.bbox[3] - chair.bbox[1]);
  const cw = (chair.bbox[2] - chair.bbox[0]) * k;
  const wx = cw + H * 0.3;
  const wy = (H - WORDMARK.height) / 2 - WORDMARK.top;
  const width = wx + WORDMARK.width;
  return {
    viewBox: `0 0 ${r2(width)} ${r2(H)}`,
    width,
    height: H,
    mark: `translate(${r2(-chair.bbox[0] * k)} ${r2(-chair.bbox[1] * k)}) scale(${Math.round(k * 1e4) / 1e4})`,
    word: `translate(${r2(wx)} ${r2(wy)})`,
    k,
  };
}

/** Stacked lockup, as in the concept: the chair (0.7 of CAO's width) centred above the wordmark, 0.41 cap heights
 *  clear of it. Word units. */
export function stackedLockup(chair: ChairArt = CHAIR): Lockup {
  const cw = CAO.width * 0.7;
  const k = cw / (chair.bbox[2] - chair.bbox[0]);
  const ch = (chair.bbox[3] - chair.bbox[1]) * k;
  const W = Math.max(cw, WORDMARK.width);
  const wy = ch + 41 - WORDMARK.top;
  const height = ch + 41 + WORDMARK.height;
  return {
    viewBox: `0 0 ${r2(W)} ${r2(height)}`,
    width: W,
    height,
    mark: `translate(${r2((W - cw) / 2 - chair.bbox[0] * k)} ${r2(-chair.bbox[1] * k)}) scale(${Math.round(k * 1e4) / 1e4})`,
    word: `translate(${r2((W - WORDMARK.width) / 2)} ${r2(wy)})`,
    k,
  };
}

export const H_LOCKUP = horizontalLockup(CHAIR);
export const S_LOCKUP = stackedLockup(CHAIR);
/** In a stacked lockup the chair is this share of the height (decides the small chair). */
export const STACKED_CHAIR_SHARE = (CHAIR.bbox[3] - CHAIR.bbox[1]) * S_LOCKUP.k / S_LOCKUP.height;

/* ------------------------------------------------------------------ colours */

export const NAVY = "#0C1120"; // --cao-night-950: the ground
export const PAPER = "#F5EFE6"; // the mark's off-white
export const TUNGSTEN = "#FFB23E"; // the seat back
export const TUNGSTEN_DEEP = "#F39A1E"; // the back's edges (the curve)
export const VIOLET_GREY = "#8A93C4"; // the tagline

/** Fixed brand colours. canvas = canvas2 means a flat back (no gradient, no id). */
export const LOGO_COLORS = {
  /** full colour on navy (the default) */
  dark: { frame: PAPER, under: "#E1DDD7", canvas: TUNGSTEN, canvas2: TUNGSTEN_DEEP, word: PAPER, tile: NAVY },
  /** full colour on light (#F2F2F2 and white) */
  light: { frame: NAVY, under: "#2B3552", canvas: TUNGSTEN, canvas2: TUNGSTEN_DEEP, word: NAVY, tile: "#F2F2F2" },
  /** monochrome on near-black: the back in silver */
  silver: { frame: PAPER, under: "#E1DDD7", canvas: "#E9EAEE", canvas2: "#B9BDC7", word: PAPER, tile: "#0A0B0E" },
  /** one colour, for stamping, etching, embroidery, single-ink print */
  white: { frame: "#FFFFFF", under: "#FFFFFF", canvas: "#FFFFFF", canvas2: "#FFFFFF", word: "#FFFFFF", tile: "transparent" },
  navy: { frame: NAVY, under: NAVY, canvas: NAVY, canvas2: NAVY, word: NAVY, tile: "transparent" },
  mono: { frame: "currentColor", under: "currentColor", canvas: "currentColor", canvas2: "currentColor", word: "currentColor", tile: "transparent" },
} as const;
export type LogoTheme = keyof typeof LOGO_COLORS;
export type LogoPaint = { frame: string; under: string; canvas: string; canvas2: string };

/** The back's gradient: deeper at the ends, full Tungsten in the middle, like canvas round a curve. */
export const BACK_STOPS = [
  [0, "canvas2"],
  [0.5, "canvas"],
  [1, "canvas2"],
] as const;

/**
 * The chair as SVG markup in its 64-unit box. A curved (gradient) back needs one <linearGradient>; give each copy on
 * a page its own `id`. `cls` adds the animation hooks (cao-chair-body / cao-chair-back).
 */
export function chairSvgInner(paint: LogoPaint, chair: ChairArt = CHAIR, opts: { id?: string; cls?: boolean } = {}): string {
  const c = (name: string) => (opts.cls ? ` class="${name}"` : "");
  const id = opts.id ?? "cao-back";
  const flat = paint.canvas === paint.canvas2;
  const grad = flat
    ? ""
    : `<defs><linearGradient id="${id}" x1="0" x2="1" y1="0" y2="0">${BACK_STOPS.map(([o, k]) => `<stop offset="${o}" stop-color="${paint[k]}"/>`).join("")}</linearGradient></defs>`;
  const under = chair.under.map((d) => `<path d="${d}"/>`).join("");
  const posts = chair.posts.map((d) => `<path d="${d}"/>`).join("");
  return `${grad}<g${c("cao-chair-body")}><g fill="${paint.under}">${under}</g><g fill="${paint.frame}">${posts}<path d="${chair.over}"/></g></g><g${c("cao-chair-back")}><path fill="${flat ? paint.canvas : `url(#${id})`}" d="${chair.back}"/></g>`;
}

/** The mark as an SVG fragment in its 64-unit box. `small` draws the 32 px chair. */
export function markSvgInner(theme: LogoTheme = "dark", small = false, id?: string): string {
  return chairSvgInner(LOGO_COLORS[theme], small ? CHAIR_SMALL : CHAIR, { id });
}

/** The wordmark (CAO over STUDIO) as an SVG fragment in word units, CAO's cap top at y 0. */
export function wordSvgInner(fill: string): string {
  return `<g fill="${fill}"><path fill-rule="evenodd" d="${CAO.d}"/><path transform="${WORDMARK.studio}" d="${STUDIO.d}"/></g>`;
}
